// Layer 3 (Transformation) — the cause Pareto (a20-maintenance S2).
// Pure: no React, no engine mutations, no rounding, no money.
//
// `rows` is always the full YTD event log (`downtimeEvents()`), passed in by
// the caller — these functions never reach for the clock or the seed, so a
// reliability read can cut the same log over a different window without a
// second projection. Hours and shares are unrounded here; the UI rounds.
//
// durable: Pareto and sub-cause projection over a stoppage log (ADR-0012).

import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { addMinutes, diffMinutes, formatDayMonth } from '@/versions/v1/shared/clock.derive';
import { BASIS_END, CAUSE_CATEGORIES, DOWNTIME_ITEMS, type CauseCategoryId } from '../maintenance/downtime.seed';
import { PARETO_CUT_PCT, YTD_START } from '../maintenance/downtime-events.seed';
import { causeCategoryLabel, eventsByKind, sumHours, type DowntimeEvent } from '../maintenance/DowntimeEvents.transform';
import { valueLostTotal } from './ValueLost.transform';
import { RISK_ENTRIES } from '@/versions/v1/modules/risk/seed';
import { FAILURE_MODES } from '@/versions/v1/shared/bunker-hill-advisory-seed';

export type ParetoRow = {
  causeCategory: CauseCategoryId;
  label: string;
  hours: number;
  eventCount: number;
  /** hours ÷ total × 100 — unrounded. */
  sharePct: number;
  /** Running Σ sharePct in rank order — unrounded; the last is 100. */
  cumulativePct: number;
  /** True for every row up to and including the first whose cumulative share
   *  reaches PARETO_CUT_PCT. A cut is drawn where that band ends. */
  withinCut: boolean;
};

export type SubCauseRow = {
  /** The coded sub-cause, exactly as the log spells it. */
  subCause: string;
  hours: number;
  eventCount: number;
  /** hours ÷ this category's hours × 100 — unrounded. */
  pctOfCategory: number;
};

export type CauseKpis = {
  unplannedHours: number;
  eventCount: number;
  /** '01 Jan – 07 Jun' — the log's own window, YTD_START to the last closed
   *  production day (BASIS_END is the open day's start, so its cutoff is the
   *  day before). Formatted once here so the card and the panel subtitle
   *  cannot disagree. */
  windowLabel: string;
  /** Σ sharePct of ranks 1–3 — how concentrated the year's losses are. */
  topThreeSharePct: number;
  /** Dollars, via the register's own basis (ValueLost.transform.ts). */
  valueLost: number;
  /** Unplanned hours as a share of the item-hours the year actually ran on:
   *  the YTD analogue of `plantKpis`' availability basis (window hours × item
   *  count, less the planned shutdowns that were never production time).
   *  Deliberately not the Downtime tab's 30-day `Availability` card, which
   *  also counts idle time as unavailable — that card answers what share of
   *  planned production time the plant ran, this one what share of the year's
   *  item-hours went to breakdowns. */
  availabilityImpactPct: number;
};

/** `01 Jan – 07 Jun` for the log's window. `to` is exclusive — the last closed
 *  production day is the day before it, so the cutoff is stepped back one day
 *  in plant-local time rather than rounded off the end. Both halves go through
 *  `formatDayMonth`, the one `DD Mon` formatter (clock.derive). */
function windowLabelFor(from: SeriesTime, to: SeriesTime): string {
  const fromLabel = formatDayMonth(from);
  const toLabel = formatDayMonth(addMinutes(to, -24 * 60));
  return `${fromLabel} – ${toLabel}`;
}

/** The YTD log ranked by unplanned hours, largest first, with each row's
 *  running share. Every category in the taxonomy is present — one that carries
 *  no stoppage this year renders at 0 h rather than vanishing, because an
 *  absent row would read as an absent cause. Ties break on label so the order
 *  is stable across runs. */
export function causePareto(rows: readonly DowntimeEvent[]): ParetoRow[] {
  const unplanned = eventsByKind(rows).unplanned;
  const total = sumHours(unplanned);

  const totals = new Map<CauseCategoryId, { hours: number; eventCount: number }>(
    CAUSE_CATEGORIES.map((category) => [category.id, { hours: 0, eventCount: 0 }]),
  );
  for (const event of unplanned) {
    const bucket = totals.get(event.causeCategory);
    if (!bucket) continue;
    bucket.hours += event.hours;
    bucket.eventCount += 1;
  }

  const ranked = [...totals.entries()]
    .map(([causeCategory, bucket]) => ({
      causeCategory,
      label: causeCategoryLabel(causeCategory),
      hours: bucket.hours,
      eventCount: bucket.eventCount,
      sharePct: total > 0 ? (bucket.hours / total) * 100 : 0,
      cumulativePct: 0,
      withinCut: false,
    }))
    .sort((a, b) => b.hours - a.hours || (a.label < b.label ? -1 : a.label > b.label ? 1 : 0));

  let running = 0;
  let cutIndex = -1;
  for (let i = 0; i < ranked.length; i += 1) {
    const row = ranked[i]!;
    running += row.sharePct;
    row.cumulativePct = running;
    if (cutIndex < 0 && running >= PARETO_CUT_PCT) cutIndex = i;
  }
  for (let i = 0; i < ranked.length; i += 1) {
    ranked[i]!.withinCut = cutIndex >= 0 && i <= cutIndex;
  }
  return ranked;
}

/** One category's unplanned stoppages grouped by their coded sub-cause, ranked
 *  the same way the Pareto is. The grouping is on the log's exact `subCause`
 *  string — a sub-cause is whatever the coder entered, so the transform never
 *  normalises one spelling into another. */
/** The Pareto as the tab draws it: the ranked rows that carried a stoppage in
 *  the window. A cause with no stoppage adds nothing to the ranking or the
 *  cumulative share, and absence renders nothing, so it is left out here; the
 *  full taxonomy stays in `causePareto` for the reads that need it. */
export function causeParetoWithStops(rows: readonly DowntimeEvent[]): ParetoRow[] {
  return causePareto(rows).filter((row) => row.eventCount > 0);
}

export function subCausesFor(rows: readonly DowntimeEvent[], id: CauseCategoryId): SubCauseRow[] {
  const unplanned = eventsByKind(rows).unplanned.filter((event) => event.causeCategory === id);
  const total = sumHours(unplanned);

  const groups = new Map<string, { hours: number; eventCount: number }>();
  for (const event of unplanned) {
    const bucket = groups.get(event.subCause) ?? { hours: 0, eventCount: 0 };
    bucket.hours += event.hours;
    bucket.eventCount += 1;
    groups.set(event.subCause, bucket);
  }

  return [...groups.entries()]
    .map(([subCause, bucket]) => ({
      subCause,
      hours: bucket.hours,
      eventCount: bucket.eventCount,
      pctOfCategory: total > 0 ? (bucket.hours / total) * 100 : 0,
    }))
    .sort((a, b) => b.hours - a.hours || (a.subCause < b.subCause ? -1 : a.subCause > b.subCause ? 1 : 0));
}

export function causeKpis(rows: readonly DowntimeEvent[]): CauseKpis {
  const kinds = eventsByKind(rows);
  const pareto = causePareto(rows);
  const unplannedHours = sumHours(kinds.unplanned);
  const windowHours = diffMinutes(BASIS_END, YTD_START) / 60;
  const plannedHours = sumHours(kinds.planned);
  const availableHours = windowHours * DOWNTIME_ITEMS.length - plannedHours;

  return {
    unplannedHours,
    eventCount: kinds.unplanned.length,
    windowLabel: windowLabelFor(YTD_START, BASIS_END),
    topThreeSharePct: pareto.slice(0, 3).reduce((sum, row) => sum + row.sharePct, 0),
    valueLost: valueLostTotal(kinds.unplanned),
    availabilityImpactPct: availableHours > 0 ? (unplannedHours / availableHours) * 100 : 0,
  };
}

export type SubCauseFailureModeRef = {
  failureModeId: string;
  rpn: number;
};

/** The FMEA row behind a sub-cause, where the log holds one: a stoppage's
 *  work order links a risk-register row, whose failure mode is an FMEA row.
 *  A sub-cause with no such chain is absent from the map — the glass draws no
 *  chip for it, never a placeholder. */
export function subCauseFailureModes(
  rows: readonly DowntimeEvent[],
  id: CauseCategoryId,
): ReadonlyMap<string, SubCauseFailureModeRef> {
  const refs = new Map<string, SubCauseFailureModeRef>();
  for (const event of eventsByKind(rows).unplanned) {
    if (event.causeCategory !== id || !event.workOrderId || refs.has(event.subCause)) continue;
    const risk = RISK_ENTRIES.find((entry) => entry.linkedWorkOrder === event.workOrderId && entry.failureModeId);
    const mode = risk ? FAILURE_MODES.find((row) => row.id === risk.failureModeId) : undefined;
    if (mode) refs.set(event.subCause, { failureModeId: mode.id, rpn: mode.rpn });
  }
  return refs;
}
